import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Title } from "../../../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Title as ListTitle } from "../../../../../../themes/Default/components/List/components/Header/components/Title";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { calendarHolidaysManagement } from "../../../../../../hooks/api/CalendarHolidaysManagement";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const Holidays = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 100;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedHoliday, setSelectedHoliday] = useState([]);
    const [holidays, setHolidays] = useState();
    const { useCalendarHolidaysListMutation, useDeleteCalendarHolidaysMutation } = calendarHolidaysManagement;
    const [holidayList, { isLoading }] = useCalendarHolidaysListMutation();
    const [deleteHoliday] = useDeleteCalendarHolidaysMutation();

    const tabs = [{ title: t("calendarHolidays.title"), isActive: true }];

    const gotoNew = () => {
        navigate("/settings/calendar/holidays/add");
    };

    const gotoEdit = (holidayId) => {
        navigate("/settings/calendar/holidays/edit/" + holidayId);
    };

    const formatDateToDMY = (value) => {
        const parts = value.split("-");

        const year = parts[0];
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);

        return `${day < 10 ? `0${day}` : day}.${month < 10 ? `0${month}` : month}.${year}`;
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedHoliday((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedHoliday?.length; i++) {
                            const holidayId = selectedHoliday[i];
                            deleteHoliday(holidayId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    holidayList(filters).then((result) => {
                                        const holidays = result?.data?.records;
                                        setHolidays(holidays);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(holidayId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
            },
        );
    };

    useEffect(() => {
        if (selectedHoliday.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedHoliday]);

    useEffect(() => {
        holidayList(filters).then((result) => {
            const holidays = result?.data?.records;
            setHolidays(holidays);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [filters, holidayList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("calendarHolidays.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("calendarHolidays.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("calendar.title")} />
                    <Link title={t("calendarHolidays.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader></ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("calendarHolidays.title")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("calendarHolidays.date")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("calendarHolidays.color")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("calendarHolidays.isRecurring")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {holidays?.length > 0 &&
                                        holidays?.map((holiday, index) => {
                                            return (
                                                <TableRow key={holiday.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(holiday.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={holiday.id} onChange={() => onSelectionChange(holiday.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{holiday.title}</TableDataCell>
                                                    <TableDataCell>{formatDateToDMY(holiday.date)}</TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ backgroundColor: holiday?.color, padding: "2px 10px", borderRadius: "20%" }}></span>
                                                        &nbsp;
                                                        <span>{holiday?.color}</span>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        {holiday.isRecurring ? t("calendarHolidays.isRecurringYes") : t("calendarHolidays.isRecurringNo")}
                                                    </TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Holidays;
